import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  documentsApi,
  parseNum,
  vatFraction,
  type DocumentExtractionItem,
  type ScannedDocumentDto,
} from '../../api/documents';
import { clientRequestsApi } from '../../api/clientRequests';
import { accountsApi } from '../../api/accounts';
import { personsApi } from '../../api/persons';
import { formatMoney } from '../../utils/formatMoney';
import { useReportScreen } from '../../components/ScreenContext';
import { useIsMirror } from '../../hooks/useIsMirror';

/**
 * Dokumentai — supplier-invoice scan workflow (frozen product structure §1):
 * upload → recognition (bridge, arithmetic-crosschecked) → accountant review →
 * confirm as an UNPOSTED draft received invoice. Posting/export to Apskaita5
 * is a later version — the draft lives here, in Pirkimai.
 */

const STATUS_BADGE: Record<string, string> = {
  Uploaded: 'bg-gray-100 text-gray-600',
  Recognizing: 'bg-sky-100 text-sky-700',
  Recognized: 'bg-amber-100 text-amber-700',
  RecognitionFailed: 'bg-red-100 text-red-700',
  Confirmed: 'bg-green-100 text-green-700',
  Rejected: 'bg-gray-100 text-gray-400',
};

type ReviewLine = {
  name: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  vatRate: number; // fraction
  account: number | null;
};

export default function DocumentsPage({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const { data: documents, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: documentsApi.list,
  });

  const selected = useMemo(
    () => (documents ?? []).find(d => d.id === selectedId) ?? null,
    [documents, selectedId],
  );

  // Poll while the selected document is mid-recognition.
  useQuery({
    queryKey: ['document', selectedId],
    queryFn: () => documentsApi.getById(selectedId as number),
    enabled: selectedId !== null && selected?.status === 'Recognizing',
    refetchInterval: 3000,
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
  });
  const { data: persons } = useQuery({
    queryKey: ['persons'],
    queryFn: () => personsApi.list({ limit: 500 }),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['documents'] });
    await queryClient.invalidateQueries({ queryKey: ['document'] });
  };

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const doc = await documentsApi.upload(file);
      await documentsApi.startRecognition(doc.id);
      await documentsApi.scan(doc.id);
      return doc;
    },
    onSuccess: async doc => {
      setUploadError(null);
      setSelectedId(doc.id);
      await refresh();
    },
    onError: (err: Error) => {
      // The scan endpoint's message carries the Lithuanian-safe reason.
      setUploadError(err.message || t('documents.uploadFailed'));
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (id: number) => {
      await documentsApi.startRecognition(id);
      return documentsApi.scan(id);
    },
    onSuccess: refresh,
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => documentsApi.reject(id),
    onSuccess: refresh,
  });

  const handleFiles = (files: FileList | null) => {
    const file = files?.[0];
    if (file) uploadMutation.mutate(file);
  };

  // Screen context for the AI copilot — scan-queue counts by status.
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    for (const d of documents ?? []) {
      stats[d.status] = (stats[d.status] ?? 0) + 1;
    }
    return stats;
  }, [documents]);

  const docCount = (documents ?? []).length;
  const screenPreview = useMemo(() =>
    (documents ?? []).slice(0, 10).map(d => {
      const name = d.fileName.length > 97 ? `${d.fileName.slice(0, 97)}...` : d.fileName;
      return `${name} [${d.status}]`;
    }), [documents]);

  useReportScreen(
    `Dokumentai (${docCount} is viso, ${statusStats.Uploaded ?? 0} ikelta, ${statusStats.Recognizing ?? 0} atpazistama, ${statusStats.Recognized ?? 0} atpazinti, ${statusStats.RecognitionFailed ?? 0} atpazinimo klaida, ${statusStats.Confirmed ?? 0} patvirtinti, ${statusStats.Rejected ?? 0} atmesti)`,
    {
      recordCount: docCount,
      preview: screenPreview,
    },
  );

  // ADR-004: mirror workspaces (asmx-owned) are read-only and the scanner's
  // confirm step creates accounting drafts, so the flow cannot complete there.
  // The surface stays visible; the upload action is disabled with the notice.
  const isMirror = useIsMirror();

  return (
    <div className="w-full space-y-4">
      {/* Header — hidden when embedded inside the Sąskaitos tabs (the tab bar
          already names the surface). */}
      {!embedded && (
      <div className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('documents.title')}</h1>
        <span className="text-xs text-gray-400">{t('documents.subtitle')}</span>
      </div>
      )}

      {isMirror && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {t('documents.mirrorNotice')}
        </div>
      )}

      {/* Upload dropzone */}
      <div
        onDragOver={e => { if (!isMirror) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); if (!isMirror) handleFiles(e.dataTransfer.files); }}
        onClick={() => { if (!isMirror) fileInputRef.current?.click(); }}
        title={isMirror ? t('documents.mirrorNotice') : undefined}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          isMirror
            ? 'border-gray-200 opacity-50 cursor-not-allowed'
            : 'cursor-pointer ' + (dragOver ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50')
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={e => { handleFiles(e.target.files); e.target.value = ''; }}
        />
        {uploadMutation.isPending ? (
          <p className="text-sm font-medium text-primary-700">{t('documents.uploading')}</p>
        ) : (
          <>
            <p className="text-sm font-medium text-gray-800">{t('documents.dropTitle')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('documents.dropHint')}</p>
          </>
        )}
        {uploadError && <p className="text-xs text-red-600 mt-2">{uploadError}</p>}
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-4 items-start">
        {/* Document list */}
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">{t('documents.listTitle')}</h2>
          {isLoading && <p className="text-xs text-gray-400 py-2">{t('common.loading')}</p>}
          {(!isLoading && (documents ?? []).length === 0) && (
            <p className="text-xs text-gray-400 py-2">{t('documents.empty')}</p>
          )}
          <div className="space-y-1">
            {(documents ?? []).map(doc => (
              <button
                key={doc.id}
                onClick={() => setSelectedId(doc.id)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  selectedId === doc.id ? 'bg-primary-50 border border-primary-200' : 'hover:bg-gray-50 border border-transparent'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${STATUS_BADGE[doc.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {t(`documents.status.${doc.status}`)}
                  </span>
                  <span className="text-xs text-gray-400 tabular-nums">
                    {new Date(doc.createdAtUtc).toLocaleDateString('lt-LT')}
                  </span>
                </div>
                <div className="text-sm text-gray-800 truncate mt-0.5">{doc.fileName}</div>
                {doc.extraction?.invoice.supplierName && (
                  <div className="text-xs text-gray-400 truncate">{doc.extraction.invoice.supplierName}</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Detail / review */}
        <div>
          {!selected && (
            <div className="bg-white border border-gray-200 rounded-lg p-12 text-center text-sm text-gray-400">
              {t('documents.selectHint')}
            </div>
          )}
          {selected && (
            <DocumentPanel
              key={selected.id}
              doc={selected}
              accounts={accounts ?? []}
              persons={persons ?? []}
              onRetry={() => retryMutation.mutate(selected.id)}
              retrying={retryMutation.isPending}
              onReject={() => rejectMutation.mutate(selected.id)}
              onChanged={refresh}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Detail panel: status-aware ─────────────────────────────────────────────

function DocumentPanel({
  doc, accounts, persons, onRetry, retrying, onReject, onChanged,
}: {
  doc: ScannedDocumentDto;
  accounts: { accountNumber: number; name: string }[];
  persons: { id: number; name: string }[];
  onRetry: () => void;
  retrying: boolean;
  onReject: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();

  const header = (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${STATUS_BADGE[doc.status]}`}>
        {t(`documents.status.${doc.status}`)}
      </span>
      <h2 className="text-base font-semibold text-gray-900">{doc.fileName}</h2>
      <a
        href={documentsApi.fileUrl(doc.id)}
        target="_blank"
        rel="noreferrer"
        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
      >
        {t('documents.openFile')} ↗
      </a>
    </div>
  );

  if (doc.status === 'Uploaded' || doc.status === 'Recognizing') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
        {header}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-sky-700">
          <span className="w-4 h-4 rounded-full border-2 border-sky-600 border-t-transparent animate-spin inline-block" />
          {t('documents.recognizing')}
        </div>
      </div>
    );
  }

  if (doc.status === 'RecognitionFailed') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        {header}
        <p className="text-sm text-red-600 mt-4">{t(`documents.error.${doc.errorCode ?? 'scan_failed'}`)}</p>
        <div className="flex gap-2 mt-4">
          <button
            onClick={onRetry}
            disabled={retrying}
            className="px-4 py-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400 disabled:opacity-50"
          >
            {retrying ? t('documents.retrying') : t('documents.retry')}
          </button>
          <button
            onClick={onReject}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            {t('documents.reject')}
          </button>
        </div>
      </div>
    );
  }

  if (doc.status === 'Rejected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        {header}
        <p className="text-sm text-gray-500 mt-4">{t('documents.rejectedInfo')}</p>
      </div>
    );
  }

  if (doc.status === 'Confirmed') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8">
        {header}
        <div className="mt-4 rounded-lg bg-green-50 border border-green-100 p-4">
          <p className="text-sm font-medium text-green-800">{t('documents.confirmedTitle')}</p>
          <p className="text-xs text-green-700 mt-1">{t('documents.confirmedHint')}</p>
          <Link
            to="/invoices/received"
            className="inline-block mt-3 px-3 py-1.5 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-full hover:border-primary-400"
          >
            {t('documents.openPurchases')} →
          </Link>
        </div>
        {doc.extraction && <ExtractionSummary doc={doc} />}
      </div>
    );
  }

  return <ReviewPanel doc={doc} accounts={accounts} persons={persons} onReject={onReject} onChanged={onChanged} header={header} />;
}

// ─── Review: extracted values → editable → confirm ──────────────────────────

function ExtractionSummary({ doc }: { doc: ScannedDocumentDto }) {
  const { t } = useTranslation();
  const inv = doc.extraction?.invoice;
  if (!inv) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
      <Info label={t('documents.f.supplier')} value={inv.supplierName} />
      <Info label={t('documents.f.number')} value={inv.invoiceNumber} />
      <Info label={t('documents.f.date')} value={inv.invoiceDate} />
      <Info label={t('documents.f.total')} value={`${inv.total} ${inv.currency}`} />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-gray-400 font-semibold">{label}</div>
      <div className="text-sm text-gray-800 truncate">{value || '—'}</div>
    </div>
  );
}

function ReviewPanel({
  doc, accounts, persons, onReject, onChanged, header,
}: {
  doc: ScannedDocumentDto;
  accounts: { accountNumber: number; name: string }[];
  persons: { id: number; name: string }[];
  onReject: () => void;
  onChanged: () => Promise<void>;
  header: React.ReactNode;
}) {
  const { t } = useTranslation();
  const inv = doc.extraction?.invoice;

  const [personId, setPersonId] = useState<number>(0);
  const [content, setContent] = useState('');
  const [defaultAccount, setDefaultAccount] = useState<number>(0);
  const [defaultVatAccount, setDefaultVatAccount] = useState<number>(0);
  const [lines, setLines] = useState<ReviewLine[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [askReject, setAskReject] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [askText, setAskText] = useState('');
  const [askDone, setAskDone] = useState(false);
  const [askBusy, setAskBusy] = useState(false);

  // Seed the review form from the extraction once.
  if (!initialized && inv) {
    setInitialized(true);
    setContent(`${inv.supplierName} ${inv.invoiceNumber}`.trim());
    const source: DocumentExtractionItem[] = inv.items.length > 0
      ? inv.items
      : [{ name: inv.supplierName || t('documents.serviceLine'), unit: 'vnt.', qty: '1', unitPrice: inv.netTotal, vatRate: '', sum: inv.netTotal }];
    setLines(source.map(it => ({
      name: it.name,
      unit: it.unit || 'vnt.',
      quantity: parseNum(it.qty) || 1,
      unitPrice: parseNum(it.unitPrice) || parseNum(it.sum) / (parseNum(it.qty) || 1),
      vatRate: it.vatRate ? vatFraction(it.vatRate)
        : (parseNum(inv.totalVat) > 0 && parseNum(inv.netTotal) > 0 ? parseNum(inv.totalVat) / parseNum(inv.netTotal) : 0),
      account: null,
    })));
  }

  const crosscheck = doc.extraction?.crosscheck ?? [];
  const nonEur = !!inv && inv.currency !== '' && inv.currency.toUpperCase() !== 'EUR';
  const totalNet = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
  const totalVat = lines.reduce((s, l) => s + l.unitPrice * l.quantity * l.vatRate, 0);
  const canConfirm =
    personId > 0 && (defaultAccount > 0 || lines.every(l => (l.account ?? 0) > 0)) && lines.length > 0 && !nonEur && !confirming;

  const confirm = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      await documentsApi.confirm({
        documentId: doc.id,
        personId,
        content: content || null,
        defaultExpenseAccount: defaultAccount,
        defaultVatAccount: defaultVatAccount > 0 ? defaultVatAccount : null,
        lines: lines.map(l => ({
          name: l.name,
          unit: l.unit,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          vatRate: l.vatRate,
          sum: l.unitPrice * l.quantity,
          account: l.account,
          vatAccount: null,
        })),
      });
      await onChanged();
    } catch (err) {
      setConfirmError((err as Error).message || t('documents.confirmFailed'));
    } finally {
      setConfirming(false);
    }
  };

  const accountOptions = accounts.map(a => (
    <option key={a.accountNumber} value={a.accountNumber}>{a.accountNumber} {a.name}</option>
  ));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      {header}

      {inv?.note && (
        <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-800">{inv.note}</div>
      )}
      {crosscheck.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
          <p className="text-xs font-semibold text-amber-800 mb-1">{t('documents.crosscheckTitle')}</p>
          {crosscheck.map((c, i) => (
            <p key={i} className="text-xs text-amber-700">⚠ {t(`documents.check.${c.check}`)} — {c.detail}</p>
          ))}
        </div>
      )}
      {nonEur && (
        <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
          {t('documents.nonEur', { currency: inv?.currency })}
        </div>
      )}

      {/* Read-only extraction facts */}
      {inv && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Info label={t('documents.f.supplier')} value={inv.supplierName} />
          <Info label={t('documents.f.number')} value={inv.invoiceNumber} />
          <Info label={t('documents.f.date')} value={inv.invoiceDate} />
          <Info label={t('documents.f.net')} value={inv.netTotal} />
          <Info label={t('documents.f.total')} value={`${inv.total} ${inv.currency}`} />
        </div>
      )}

      {/* Review controls */}
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1 font-medium">{t('documents.f.person')} *</span>
          <select
            value={personId}
            onChange={e => setPersonId(Number(e.target.value))}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value={0}>— {t('documents.choosePerson')} —</option>
            {persons.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1 font-medium">{t('documents.f.content')}</span>
          <input
            value={content}
            onChange={e => setContent(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1 font-medium">{t('documents.f.defaultAccount')} *</span>
          <select
            value={defaultAccount}
            onChange={e => setDefaultAccount(Number(e.target.value))}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value={0}>—</option>
            {accountOptions}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-gray-500 mb-1 font-medium">{t('documents.f.defaultVatAccount')}</span>
          <select
            value={defaultVatAccount}
            onChange={e => setDefaultVatAccount(Number(e.target.value))}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            <option value={0}>—</option>
            {accountOptions}
          </select>
        </label>
      </div>

      {/* Lines */}
      <div className="border border-gray-100 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <th className="px-2 py-2 font-semibold">{t('documents.col.name')}</th>
              <th className="px-2 py-2 font-semibold w-20">{t('documents.col.unit')}</th>
              <th className="px-2 py-2 font-semibold w-20">{t('documents.col.qty')}</th>
              <th className="px-2 py-2 font-semibold w-28">{t('documents.col.price')}</th>
              <th className="px-2 py-2 font-semibold w-20">{t('documents.col.vat')}</th>
              <th className="px-2 py-2 font-semibold w-28">{t('documents.col.sum')}</th>
              <th className="px-2 py-2 font-semibold min-w-[160px]">{t('documents.col.account')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-2 py-1.5">
                  <input
                    value={l.name}
                    onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    className="w-full border border-transparent hover:border-gray-200 focus:border-primary-300 rounded px-1 py-0.5 text-sm"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    value={l.unit}
                    onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))}
                    className="w-full border border-transparent hover:border-gray-200 focus:border-primary-300 rounded px-1 py-0.5 text-sm"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="number" step="0.01" min="0" value={l.quantity}
                    onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, quantity: Number(e.target.value) || 0 } : x))}
                    className="w-full border border-transparent hover:border-gray-200 focus:border-primary-300 rounded px-1 py-0.5 text-sm tabular-nums"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <input
                    type="number" step="0.0001" min="0" value={l.unitPrice}
                    onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, unitPrice: Number(e.target.value) || 0 } : x))}
                    className="w-full border border-transparent hover:border-gray-200 focus:border-primary-300 rounded px-1 py-0.5 text-sm tabular-nums"
                  />
                </td>
                <td className="px-2 py-1.5">
                  <select
                    value={l.vatRate}
                    onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, vatRate: Number(e.target.value) } : x))}
                    className="w-full border border-gray-200 rounded px-1 py-0.5 text-sm bg-white tabular-nums"
                  >
                    <option value={0}>0%</option>
                    <option value={0.05}>5%</option>
                    <option value={0.09}>9%</option>
                    <option value={0.21}>21%</option>
                  </select>
                </td>
                <td className="px-2 py-1.5 text-gray-800 tabular-nums">{formatMoney(l.unitPrice * l.quantity)}</td>
                <td className="px-2 py-1.5">
                  <select
                    value={l.account ?? 0}
                    onChange={e => setLines(prev => prev.map((x, j) => j === i ? { ...x, account: Number(e.target.value) || null } : x))}
                    className="w-full border border-gray-200 rounded px-1 py-0.5 text-sm bg-white"
                  >
                    <option value={0}>{defaultAccount > 0 ? t('documents.col.defaultAccount') : '—'}</option>
                    {accountOptions}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {t('documents.totals')}: <span className="font-medium text-gray-900 tabular-nums">{formatMoney(totalNet)}</span>
          {' '}+ {t('documents.vat')} <span className="font-medium text-gray-900 tabular-nums">{formatMoney(totalVat)}</span>
          {' '}= <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(totalNet + totalVat)}</span>
        </div>
        <div className="flex items-center gap-2">
          {askReject ? (
            <>
              <span className="text-xs text-gray-500">{t('documents.rejectConfirm')}</span>
              <button onClick={onReject} className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100">
                {t('common.yes')}
              </button>
              <button onClick={() => setAskReject(false)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                {t('common.no')}
              </button>
            </>
          ) : (
            <button onClick={() => setAskReject(true)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              {t('documents.reject')}
            </button>
          )}
          <button
            onClick={confirm}
            disabled={!canConfirm}
            title={!canConfirm && personId === 0 ? t('documents.needPerson') : undefined}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {confirming ? t('documents.confirming') : t('documents.confirm')}
          </button>
        </div>
      </div>
      {confirmError && <p className="text-xs text-red-600">{confirmError}</p>}

      {/* Paklausti kliento — queues a question in Laukiama kliento (Dėmesys) */}
      <div className="border-t border-gray-100 pt-3">
        {askDone ? (
          <p className="text-xs text-green-700">{t('documents.asked')}</p>
        ) : askOpen ? (
          <div className="space-y-2">
            <textarea
              value={askText}
              onChange={e => setAskText(e.target.value)}
              placeholder={t('documents.askPlaceholder')}
              rows={2}
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setAskOpen(false)} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  if (!askText.trim()) return;
                  setAskBusy(true);
                  try {
                    await clientRequestsApi.create({
                      kind: 'Question',
                      text: askText.trim(),
                      scannedDocumentId: doc.id,
                    });
                    setAskDone(true);
                  } finally {
                    setAskBusy(false);
                  }
                }}
                disabled={askBusy || !askText.trim()}
                className="px-4 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400 disabled:opacity-40"
              >
                {askBusy ? t('common.saving') : t('documents.askSend')}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAskOpen(true)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
            {t('documents.askClient')} →
          </button>
        )}
      </div>

      <p className="text-[11px] text-gray-400">{t('documents.confirmHint')}</p>
    </div>
  );
}
